import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import InquiryForm from '../components/InquiryForm';
import { brand } from '../config/brand';

const SITE = 'https://www.uclipse.com';
const CANONICAL = `${SITE}/kid-safe`;

type Product = {
  no: string;
  sku: string;
  name: string;
  short: string;
  image: string;
  lead: string;
  ph: string;
  actives: string;
  points: { h: string; p: string }[];
  ingredients: { name: string; desc: string }[];
};

const products: Product[] = [
  {
    no: '01',
    sku: 'UKS-FC',
    name: 'Kid-Safe Bioenzyme Floor Cleaner',
    short: 'Deep porosity soil degradation',
    image: '/kid-safe/floor-cleaner.jpg',
    lead: 'A water-thin, high-wetting concentrate for deep biological soil removal across all water-safe flooring.',
    ph: '6.0 – 6.5',
    actives: 'Plant Glucosides + Neem + Micro-filtered Bioenzymes',
    points: [
      {
        h: 'The problem',
        p: 'Standard cleaners leave a sticky, chemical-laden film that crawling toddlers absorb through skin or hand-to-mouth contact.',
      },
      {
        h: 'The biological advantage',
        p: 'Active liquid bioenzymes penetrate microscopic pores and grout, digesting trapped starches and grease long after mopping.',
      },
      {
        h: 'Natural defence system',
        p: 'Botanical neem extract with pine and lemongrass oils deliver antimicrobial protection and fresh air — with no synthetic biocides.',
      },
      {
        h: 'Surface freedom',
        p: 'Dries clean with zero streak or residue. Safe for hardwood, marble, granite, vinyl, tiles and laminate.',
      },
    ],
    ingredients: [
      { name: 'Aqua (Demineralized Water)', desc: 'Pure solvent base to ensure formula stability.' },
      { name: 'Plant-Derived Non-Ionic Surfactants (Alkyl Polyglucosides)', desc: 'Ultra-mild, biodegradable cleansing agents sourced from natural coconut and corn sugars.' },
      { name: 'Active Fermented Bioenzymes', desc: 'Natural biological proteins engineered to target and digest organic stains and soils at the microscopic level.' },
      { name: 'Natural Botanical Antibacterial Matrix (Azadirachta indica Extract)', desc: 'Plant-derived organic compound offering broad-spectrum surface protection.' },
      { name: 'Natural Essential Oil Blend (Pine & Lemongrass)', desc: 'Pure plant extracts providing a crisp, natural aroma and complementary antimicrobial properties.' },
      { name: 'Sodium Citrate', desc: 'Food-grade water softener and chelating agent derived from citrus fruits.' },
      { name: 'Eco-Certified Preservation System', desc: 'Mild, food-grade organic salts used to maintain product freshness and prevent microbial growth.' },
      { name: 'Citric Acid', desc: 'Natural pH regulator used to balance the formula safely for skin and floor surfaces.' },
    ],
  },
  {
    no: '02',
    sku: 'UKS-FS',
    name: 'Ultra-Gentle Baby Fabric Softener',
    short: 'Fibre breathability & friction care',
    image: '/kid-safe/fabric-softener.jpg',
    lead: 'A low-viscosity, milky emulsion that softens baby garments and bedding while eliminating embedded milk odours.',
    ph: '3.5 – 4.5',
    actives: 'Plant Esterquats + Glycerin-Stabilised Bioenzymes',
    points: [
      {
        h: 'The problem',
        p: 'Commercial softeners coat fabric in silicone or quats, clogging pores, destroying breathability and triggering eczema.',
      },
      {
        h: 'The biological advantage',
        p: 'Low-pH proteases, amylases and lipases degrade spit-up oils and milk-protein crusts inside the weave, removing odour permanently.',
      },
      {
        h: 'The softening matrix',
        p: 'A sustainable plant-derived esterquat matrix aligns fibres — clothes stay fluffy, absorbent, static-free and breathable.',
      },
      {
        h: 'Skin-chafing prevention',
        p: 'Dramatically lowers fabric-to-skin friction, protecting raw or sensitive baby skin during movement.',
      },
    ],
    ingredients: [
      { name: 'Aqua (Demineralized Water)', desc: 'Pure solvent base forming the continuous emulsion phase.' },
      { name: 'Plant-Based Cationic Lipid Matrix (Esterquat)', desc: 'Sustainable, rapidly biodegradable conditioning agent derived from vegetable oils to smooth and fluff fabric fibers.' },
      { name: 'Active Liquid Bioenzyme Complex', desc: 'Targeted biological catalysts that break down complex food, formula, and organic clothing odors.' },
      { name: 'Vegetable Glycerin', desc: 'Plant-derived humectant used to condition fabrics and naturally stabilize the active enzymes.' },
      { name: 'Hypoallergenic Allergen-Free Fragrance', desc: 'A strictly vetted, gentle aroma profile engineered to be free of skin-sensitizing allergens.' },
      { name: 'Eco-Certified Preservation System', desc: 'A synergistic blend of non-toxic, food-grade preservatives to protect the emulsion from microbial contamination.' },
      { name: 'Calcium Chloride', desc: 'Natural mineral salt utilized to optimize liquid pouring consistency.' },
      { name: 'Citric Acid', desc: 'Natural, citrus-derived organic acid utilized to secure a stable, fiber-safe pH level.' },
    ],
  },
  {
    no: '03',
    sku: 'UKS-LD',
    name: 'Ultra-Mild Baby Liquid Detergent',
    short: 'Triple-enzyme stain & protein extraction',
    image: '/kid-safe/liquid-detergent.jpg',
    lead: 'A skin-neutral, high-performance laundry liquid optimised for HE and conventional washing machines.',
    ph: '6.5 – 7.5 (skin-neutral)',
    actives: 'Triple-Enzyme Complex + Non-ionic Plant Surfactants',
    points: [
      {
        h: 'The problem',
        p: 'Standard powders and liquids leave hard mineral crusts and optical brighteners that trigger severe newborn reactions.',
      },
      {
        h: 'The biological advantage',
        p: 'A concentrated triple-enzyme system (protease, amylase, lipase) liquefies organic soils, formula residues and starches without wear.',
      },
      {
        h: 'Synergistic plant glucosides',
        p: 'Coco-Glucoside, Lauryl Glucoside and Cocamidopropyl Betaine lift dirt gently and rinse 100% clean.',
      },
      {
        h: 'Natural fibre shield',
        p: 'Sodium citrate binds hard-water minerals, preventing greying and stiffening — with no aggressive synthetic builders.',
      },
    ],
    ingredients: [
      { name: 'Aqua (Demineralized Water)', desc: 'Pure solvent base used to seamlessly blend the cleaning actives.' },
      { name: 'Plant-Derived Non-Ionic Surfactants (Alkyl Polyglucosides)', desc: 'Ultra-mild, highly biodegradable cleansing agents sourced from renewable coconut and corn sugars to lift surface soils.' },
      { name: 'Plant-Derived Amphoteric Surfactant (Coco-Betaine Matrix)', desc: 'A gentle, coconut-derived compound that boosts mildness and creates a rich, skin-safe lather.' },
      { name: 'Triple-Action Active Bioenzyme Complex', desc: 'Targeted biological catalysts (including Protease, Amylase, and Lipase) that split and dissolve complex milk, formula, and food stains.' },
      { name: 'Vegetable Glycerin', desc: 'Plant-derived humectant used to condition fabric fibers and naturally protect enzyme viability on the shelf.' },
      { name: 'Sodium Citrate', desc: 'Food-grade water softener derived from citrus fruits to bind hard water minerals and prevent fabric graying.' },
      { name: 'Hypoallergenic Allergen-Free Fragrance', desc: 'A strictly vetted, gentle aroma profile engineered to be free of skin-sensitizing allergens.' },
      { name: 'Eco-Certified Preservation System', desc: 'A synergistic blend of non-toxic, food-grade organic salts used to protect the formula from microbial growth.' },
      { name: 'Citric Acid', desc: 'Natural, citrus-derived organic acid used to maintain a completely skin-neutral pH level.' },
    ],
  },
];

const charter = [
  {
    v: '100%',
    good: true,
    h: 'Active Bioenzymes',
    p: "Nature's catalysts cut grease, fats, milk proteins and formula oils at the source.",
  },
  {
    v: '0%',
    good: false,
    h: 'Sulfates (SLS, SLES, ALS)',
    p: 'Eliminates harsh stripping agents that disrupt the delicate skin barrier.',
  },
  {
    v: '0%',
    good: false,
    h: 'Harsh Quats & Silicones',
    p: "Replaced with rapidly biodegradable, plant-based lipid matrices that won't trap heat or cause dermatitis.",
  },
  {
    v: '0%',
    good: false,
    h: 'Optical Brighteners & Synthetic Dyes',
    p: 'Removes persistent UV-reflecting residues and needless ingestion risks.',
  },
  {
    v: '0%',
    good: false,
    h: 'Parabens, Alcohols & Phosphates',
    p: 'Preserved with eco-certified, food-grade tech — zero toxic vapour or aquatic runoff.',
  },
];

const commercial = [
  {
    stat: '18–24',
    unit: 'months',
    label: 'Premium ingredient stability',
    body: 'A specialised glycerin/citrate stabilising shield keeps enzymes active throughout an 18-to-24-month shelf life.',
  },
  {
    stat: '0',
    unit: 'formaldehyde',
    label: 'Eco-preservation system',
    body: 'Food-grade sodium benzoate and potassium sorbate guard against mould, yeast and bacteria — no formaldehyde-releasers.',
  },
  {
    stat: 'Lower',
    unit: 'cost per use',
    label: 'Concentrated value',
    body: 'High active matter means low dosage per use — lowering carbon footprint, packaging waste and cost-per-use.',
  },
];

const clientValue = [
  {
    h: 'Turnkey portfolio',
    p: 'Three complementary, universally needed home-care SKUs — ready to market under a single, cohesive brand message.',
  },
  {
    h: 'Uncompromising testing',
    p: 'Every product is designed to pass rigorous environmental and dermatological tests, including OECD and CIPAC standards.',
  },
  {
    h: 'Market-ready labeling',
    p: 'Clear, transparent ingredient listings give parents total peace of mind and build lifelong brand trust.',
  },
];

const faqs = [
  {
    q: 'Are Uclipse Kid-Safe products really safe for babies and toddlers?',
    a: 'Yes. Every product in the Uclipse Kid-Safe suite is manufactured to a zero-tolerance chemical charter — free from sulfates (SLS/SLES/ALS), harsh quats, silicones, optical brighteners, synthetic dyes, parabens, alcohols and phosphates — making it safe for crawling infants, toddlers and pets.',
  },
  {
    q: 'What makes the cleaning "biological"?',
    a: 'The formulas use active plant-derived bioenzymes — proteases, amylases and lipases — that digest grease, milk proteins, formula oils and starches at the source, together with non-ionic plant surfactants and glucosides that lift and rinse away soil.',
  },
  {
    q: 'Which products are in the Uclipse Kid-Safe range?',
    a: 'Three: a Kid-Safe Bioenzyme Floor Cleaner, an Ultra-Gentle Baby Fabric Softener and an Ultra-Mild Baby Liquid Detergent — all built on one 100% plant-derived, skin-neutral base.',
  },
  {
    q: 'Is the baby detergent suitable for washing machines?',
    a: 'Yes. The Ultra-Mild Baby Liquid Detergent is optimised for both HE (high-efficiency) and conventional washing machines, and rinses 100% clean without leaving mineral crusts or optical-brightener residue.',
  },
  {
    q: 'Are the products eco-friendly and biodegradable?',
    a: 'Yes. They are 100% plant-derived, preserved with food-grade sodium benzoate and potassium sorbate (no formaldehyde-releasers), and designed to meet OECD biodegradability standards with zero toxic vapour or aquatic runoff.',
  },
  {
    q: 'What is the shelf life of the Kid-Safe range?',
    a: 'A glycerin/citrate stabilising system keeps the enzymes active for an 18-to-24-month shelf life.',
  },
  {
    q: 'Is the floor cleaner safe for wooden and marble floors?',
    a: 'Yes. It dries with zero streak or residue and is safe for hardwood, marble, granite, vinyl, tiles and laminate.',
  },
  {
    q: 'Can I get these products under my own private label?',
    a: `Yes. Uclipse — a brand of ${brand.legalEntity} — offers full private-label and contract manufacturing with custom branding, fragrance and packaging. Contact us for MOQ, packaging options and a quotation.`,
  },
];

export default function KidSafePage() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Kid-Safe Home Care', item: CANONICAL },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Uclipse Kid-Safe Eco-Friendly Home Care Suite',
      description:
        'A three-tier, 100% plant-derived, skin-neutral home-care range for families, childcare and pediatric settings.',
      itemListElement: products.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Product',
          name: p.name,
          sku: p.sku,
          description: p.lead,
          image: `${SITE}${p.image}`,
          category: 'Kid-Safe Home Care',
          brand: { '@type': 'Brand', name: 'Uclipse' },
          manufacturer: { '@type': 'Organization', name: brand.legalEntity },
          additionalProperty: [
            { '@type': 'PropertyValue', name: 'Target pH', value: p.ph },
            { '@type': 'PropertyValue', name: 'Key actives', value: p.actives },
            {
              '@type': 'PropertyValue',
              name: 'Ingredients',
              value: p.ingredients.map((ing) => ing.name).join('; '),
            },
          ],
        },
      })),
    },
  ];

  return (
    <>
      <Seo
        title="Kid-Safe & Baby Home Care — Bioenzyme Floor Cleaner, Detergent & Softener"
        description="Uclipse Kid-Safe is a 100% plant-derived, skin-neutral home-care suite — bioenzyme floor cleaner, ultra-mild baby liquid detergent and ultra-gentle baby fabric softener. Free from sulfates, quats, optical brighteners, parabens, dyes & phosphates. Safe for babies, toddlers, pets, childcare and pediatric settings."
        canonicalUrl={CANONICAL}
        image={`${SITE}/kid-safe/og.jpg`}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-brand-50">
        <div className="container-page grid items-center gap-10 py-16 md:grid-cols-2 md:py-24">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-green/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-green-700">
              New · Product Portfolio 2026
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-tight text-slate-900 md:text-5xl">
              Kid-Safe &amp; eco-friendly{' '}
              <span className="text-accent-green">home care</span>, powered by bioenzymes
            </h1>
            <p className="mt-5 max-w-xl text-lg text-slate-600">
              High-performance biological cleaning with a{' '}
              <strong className="text-slate-800">100% plant-derived, skin-neutral, clean-label promise</strong> —
              engineered for families, childcare facilities and pediatric settings.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#enquire" className="btn bg-accent-green text-white hover:bg-green-600">
                Request a Quote
              </a>
              <a href="#suite" className="btn-secondary">
                Explore the Suite
              </a>
            </div>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-slate-600">
              <li className="flex items-center gap-1.5"><span className="text-accent-green">✓</span> Sulfate &amp; paraben free</li>
              <li className="flex items-center gap-1.5"><span className="text-accent-green">✓</span> No optical brighteners</li>
              <li className="flex items-center gap-1.5"><span className="text-accent-green">✓</span> Dermatologically minded</li>
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-accent-green/20 to-brand-200/30 blur-2xl" />
            <img
              src="/kid-safe/hero.jpg"
              alt="Mother holding a happy, healthy baby in a clean home — Uclipse Kid-Safe home care"
              className="relative mx-auto max-h-[520px] w-full rounded-2xl object-cover shadow-card ring-1 ring-slate-100"
              width={1000}
              height={1460}
            />
          </div>
        </div>
      </section>

      {/* Quick answer / definition block — optimised for answer engines */}
      <section className="border-y border-slate-100 bg-white">
        <div className="container-page max-w-4xl py-10">
          <p className="text-lg leading-relaxed text-slate-700">
            <strong className="text-slate-900">Uclipse Kid-Safe</strong> is a next-generation, eco-friendly home-care
            suite from <strong>{brand.legalEntity}</strong>. It pairs advanced <em>biological</em> (bioenzyme)
            cleaning with an ultra-strict clean-label formula that is 100% plant-derived and skin-neutral. The range
            covers three everyday essentials — a <Link to="#suite" className="text-brand-600 hover:underline">bioenzyme
            floor cleaner</Link>, an <Link to="#suite" className="text-brand-600 hover:underline">ultra-mild baby
            liquid detergent</Link> and an <Link to="#suite" className="text-brand-600 hover:underline">ultra-gentle
            baby fabric softener</Link> — each free from the aggressive sulfates, quats, optical brighteners,
            synthetic dyes, parabens and phosphates linked to infant eczema, respiratory irritation and contact
            allergies.
          </p>
        </div>
      </section>

      {/* Why / market opportunity */}
      <section className="container-page py-16 md:py-20">
        <div className="grid gap-10 md:grid-cols-2 md:gap-14">
          <div>
            <span className="text-sm font-semibold uppercase tracking-wide text-accent-green">The opportunity</span>
            <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 md:text-4xl">
              A clean-label answer to a real parental demand
            </h2>
            <p className="mt-5 text-slate-600">
              Modern parents demand uncompromising cleanliness without developmental or dermatological hazards at home.
              Standard formulations rely on aggressive sulfates, harsh quats, optical brighteners and synthetic dyes
              that linger on surfaces and fabrics — primary triggers for infant eczema, respiratory irritation and
              contact allergies.
            </p>
            <p className="mt-4 text-slate-600">
              The Uclipse Kid-Safe suite is a high-performance, three-tier portfolio engineered for family
              environments — balancing advanced biological cleaning with an ultra-strict clean-label philosophy.
            </p>
          </div>
          <div className="overflow-hidden rounded-2xl">
            <img
              src="/kid-safe/folded-clothes.jpg"
              alt="Soft, freshly laundered baby clothes folded beside a houseplant"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Safety charter */}
      <section className="bg-gradient-to-b from-emerald-50/60 to-white py-16 md:py-20">
        <div className="container-page">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="overflow-hidden rounded-2xl lg:sticky lg:top-24">
              <img
                src="/kid-safe/plant-derived.jpg"
                alt="A pure water droplet resting on a sage leaf, symbolising 100% plant-derived ingredients"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
            <div>
              <span className="text-sm font-semibold uppercase tracking-wide text-accent-green">Safety charter</span>
              <h2 className="mt-2 font-display text-3xl font-bold text-slate-900 md:text-4xl">
                A zero-tolerance chemical charter
              </h2>
              <p className="mt-4 text-slate-600">
                Every product is manufactured to guarantee absolute safety for crawling infants, toddlers and pets.
              </p>
              <div className="mt-8 space-y-3">
                {charter.map((c) => (
                  <div
                    key={c.h}
                    className="flex items-start gap-4 rounded-xl bg-white p-4 shadow-card ring-1 ring-slate-100"
                  >
                    <div
                      className={`grid h-14 w-14 flex-shrink-0 place-items-center rounded-lg font-display text-lg font-bold ${
                        c.good ? 'bg-accent-green/10 text-green-700' : 'bg-brand-50 text-brand-700'
                      }`}
                    >
                      {c.v}
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-slate-900">{c.h}</h3>
                      <p className="mt-0.5 text-sm text-slate-600">{c.p}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The suite — 3 products */}
      <section id="suite" className="container-page py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">
            One biological platform, three home-care heroes
          </h2>
          <p className="mt-3 text-slate-600">
            Complementary SKUs built on a shared 100% plant-derived, skin-neutral base.
          </p>
        </div>

        <div className="mt-14 space-y-16">
          {products.map((p, i) => (
            <article
              key={p.sku}
              className="grid items-center gap-8 md:grid-cols-2 md:gap-12"
              id={p.sku.toLowerCase()}
            >
              <div className={`overflow-hidden rounded-2xl shadow-card ring-1 ring-slate-100 ${i % 2 ? 'md:order-2' : ''}`}>
                <img
                  src={p.image}
                  alt={`${p.name} — ${p.short}`}
                  className="h-full max-h-[460px] w-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className={i % 2 ? 'md:order-1' : ''}>
                <div className="flex items-center gap-3">
                  <span className="font-display text-4xl font-bold text-accent-green/30">{p.no}</span>
                  <span className="rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-700">
                    {p.short}
                  </span>
                </div>
                <h3 className="mt-3 font-display text-2xl font-bold text-slate-900">{p.name}</h3>
                <p className="mt-2 text-slate-600">{p.lead}</p>

                <dl className="mt-5 space-y-3">
                  {p.points.map((pt) => (
                    <div key={pt.h} className="border-l-2 border-accent-green/40 pl-4">
                      <dt className="text-sm font-semibold text-slate-900">{pt.h}</dt>
                      <dd className="mt-0.5 text-sm text-slate-600">{pt.p}</dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-5 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-md bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                    Target pH {p.ph}
                  </span>
                  <span className="rounded-md bg-slate-100 px-2.5 py-1 font-medium text-slate-700">{p.actives}</span>
                </div>

                <details className="group mt-6 overflow-hidden rounded-xl border border-emerald-100 bg-emerald-50/40 open:bg-emerald-50/60">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-emerald-900 marker:content-none">
                    <span className="flex items-center gap-2">
                      <svg className="h-4 w-4 text-accent-green" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Full ingredient transparency ({p.ingredients.length})
                    </span>
                    <svg className="h-4 w-4 flex-shrink-0 text-emerald-700 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M5.3 7.3a1 1 0 011.4 0L10 10.6l3.3-3.3a1 1 0 111.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4z" clipRule="evenodd" />
                    </svg>
                  </summary>
                  <ul className="space-y-3 border-t border-emerald-100 bg-white/70 px-4 py-4">
                    {p.ingredients.map((ing) => (
                      <li key={ing.name} className="flex gap-3">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent-green" aria-hidden="true" />
                        <div>
                          <span className="text-sm font-semibold text-slate-900">{ing.name}</span>
                          <span className="text-sm text-slate-600"> — {ing.desc}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Technical matrix */}
      <section className="bg-slate-50 py-16 md:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">Portfolio technical matrix</h2>
            <p className="mt-3 text-slate-600">
              Designed to pass OECD biodegradability standards, CIPAC accelerated thermal-stability testing and rigorous
              dermatological screening.
            </p>
          </div>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse overflow-hidden rounded-xl bg-white text-left text-sm shadow-card ring-1 ring-slate-100">
              <thead>
                <tr className="bg-brand-600 text-white">
                  <th className="px-5 py-3 font-semibold">Product</th>
                  <th className="px-5 py-3 font-semibold">Target pH</th>
                  <th className="px-5 py-3 font-semibold">Key active mechanism</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => (
                  <tr key={p.sku} className={i % 2 ? 'bg-slate-50/60' : 'bg-white'}>
                    <td className="px-5 py-4 font-semibold text-slate-900">{p.name}</td>
                    <td className="px-5 py-4 text-slate-700">{p.ph}</td>
                    <td className="px-5 py-4 text-slate-700">{p.actives}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Commercial positioning */}
      <section className="container-page py-16 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">
            Built for the shelf, the planet and the margin
          </h2>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {commercial.map((c) => (
            <div key={c.label} className="card p-7">
              <div className="font-display text-4xl font-bold text-accent-green">{c.stat}</div>
              <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{c.unit}</div>
              <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{c.label}</h3>
              <p className="mt-2 text-sm text-slate-600">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Client / brand value */}
      <section className="bg-slate-50 py-16 md:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-3xl font-bold text-slate-900 md:text-4xl">
              A turnkey edge in the green consumer sector
            </h2>
            <p className="mt-3 text-slate-600">
              Adopt a cohesive home-care suite and gain an immediate advantage in a rapidly expanding market.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {clientValue.map((v, i) => (
              <div key={v.h} className="card p-7">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-brand-600 font-display font-bold text-white">
                  {i + 1}
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{v.h}</h3>
                <p className="mt-2 text-sm text-slate-600">{v.p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — Answer Engine Optimised */}
      <section className="container-page py-16 md:py-20">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center font-display text-3xl font-bold text-slate-900 md:text-4xl">
            Frequently asked questions
          </h2>
          <div className="mt-10 divide-y divide-slate-100 overflow-hidden rounded-xl bg-white shadow-card ring-1 ring-slate-100">
            {faqs.map((f) => (
              <details key={f.q} className="group px-6 py-5 open:bg-slate-50/50">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-display font-semibold text-slate-900">
                  {f.q}
                  <span className="text-accent-green transition-transform group-open:rotate-45">＋</span>
                </summary>
                <p className="mt-3 text-slate-600">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + lead form */}
      <section className="bg-gradient-to-br from-brand-700 to-brand-500 py-16 text-white md:py-20">
        <div className="container-page text-center">
          <h2 className="font-display text-3xl font-bold md:text-4xl">Clean performance. Clear conscience.</h2>
          <p className="mx-auto mt-3 max-w-2xl text-brand-50/90">
            Launch the Uclipse Kid-Safe suite under your own brand, or stock it for your childcare, retail or
            hospitality business. Request pricing, MOQ, packaging and private-label options.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href="#enquire" className="btn bg-white text-brand-700 hover:bg-slate-100">
              Request a Quote
            </a>
            <Link to="/distributors" className="btn bg-accent-green text-white hover:bg-green-600">
              Become a Distributor
            </Link>
          </div>
        </div>
      </section>

      <section id="enquire" className="bg-slate-50 py-16">
        <div className="container-page mx-auto max-w-2xl">
          <InquiryForm
            type="quote"
            title="Enquire about the Kid-Safe range"
            description="Tell us your required products, quantities, city and whether you need private-label branding. We respond within 1 business day."
          />
        </div>
      </section>
    </>
  );
}
